/* [dAgora] Multi-Peek for Dawn Slideshow — merged anti-flicker + full controls
   - Desktop: 2 or 3列（data-cols-desktop）
   - Mobile: 1列固定
   - 矢印/サムネ/自動送り/クリック領域 対応
   - 縦スク中はループ補正・スナップを抑制してチラつき防止
*/
(function () {
  'use strict';

  const SELECTOR = '[data-dag-slider]';
  const mqDesktop = window.matchMedia('(min-width: 990px)');
  const ease = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

  const S = new WeakMap();

  const toInt = (v, d) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : d;
  };
  const on = (el, ev, fn, opt) => { el.addEventListener(ev, fn, opt); return () => el.removeEventListener(ev, fn, opt); };

  function initOne(root) {
    destroyOne(root);

    const viewport = root.querySelector('.slider');
    if (!viewport) return;

    const st = {
      root,
      viewport,
      cols: 1,
      gap: 6,
      peekD: 40,
      peekM: 16,
      arrowsMode: 'page',
      loop: root.getAttribute('data-loop') !== 'false',
      unit: 0,
      clones: 0,
      realCount: 0,
      jumping: false,
      timers: { scrollEnd: 0 },
      current: 0,
      targetX: null,
      off: [],
      peekPx: 0,
      itemW:0, // 1枚の実幅（gap を除いた中身幅）
      ready: false,      // 初期スクロール確定までは補正を抑止
      // ①由来：縦スク検知とガード（チラつき防止）
      touchStartX: 0,
      touchStartY: 0,
      isVerticalScroll: false
    };
    S.set(root, st);

    // ===== utilities =====
    const mid = () => Math.floor(st.cols / 2);
    const isDesktop = () => mqDesktop.matches;
    const qReal = () => Array.from(st.viewport.querySelectorAll('.slider__slide:not([data-clone="1"])'));
    // クローン含む全スライド
    const qSlides = () => Array.from(st.viewport.querySelectorAll('.slider__slide'));

    const leftIndexForCenter = (realIndex) => (realIndex - mid() + st.realCount) % st.realCount;
    const scrollForLeftIndex = (L) => st.peekPx + (st.clones + L) * st.unit;

    function nearestLeftIndexFromScroll(x) {
      const Lfloat = (x / st.unit) - st.clones;
      let L = Math.round(Lfloat);
      const N = st.realCount;
      L %= N;
      if (L < 0) L += N;
      return L;
    }
    const centerIndexFromScroll = (x) => {
      const L = nearestLeftIndexFromScroll(x);
      return (L + mid()) % st.realCount;
    };

    function scrollToCenterIndex(target) {
      if (st.unit <= 0 || st.realCount <= 0) return;

      const N = st.realCount;
      const span = N * st.unit;
      const curX = st.viewport.scrollLeft;

      // 左端index→Xへ
      const L = leftIndexForCenter(target);
      const xBase = scrollForLeftIndex(L);

      let xAdj = xBase;
      while (xAdj - curX > span/2) xAdj -= span;
      while (curX - xAdj > span/2) xAdj += span;

      st.jumping = true;
      st.targetX = xAdj;
      st.viewport.scrollTo({ left: xAdj, behavior: ease });

      let tries = 0;
      const done = () => {
        const dx = Math.abs(st.viewport.scrollLeft - (st.targetX ?? st.viewport.scrollLeft));
        if (dx <= 1 || tries++ > 20) {
          st.jumping = false;
          st.targetX = null;
          normalizeLoop();
          updateCurrentFromScroll();
        } else {
          setTimeout(done, 60);
        }
      };
      setTimeout(done, 80);
    }

    //2025/10/16追加
    function jumpToIndexExact(i) {
      st.jumping = true;
      const x = centerXForRealIndex(i);
      st.viewport.scrollTo({ left: x, behavior: 'auto' }); // 慣性なしで即着地
      // 終了処理
      requestAnimationFrame(() => {
        st.jumping = false;
        st.current = i;
        updateCurrentFromScroll();
        updateActiveUI();
      });
    }


    // ✅ 追加：IntersectionObserverで“見えている時だけ”自動送り
    function attachVisibilityAutoplay(st, start, stop) {
      if (!('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => {
        const e = entries[0];
        if (!e) return;
        if (e.isIntersecting && e.intersectionRatio >= 0.4) start();
        else stop();
      }, { threshold: [0, 0.4, 1] });
      io.observe(st.root);
      st.off.push(() => io.disconnect());
    }

    // 25/10/16追加：画面中心に最も近い“実スライドindex”を返す(初回スライドの表示対策)
    function centerIndexByDistance(st) {
      const slides = Array.from(st.viewport.querySelectorAll('.slider__slide:not([data-clone="1"])'));
      if (!slides.length) return 0;
      const viewRect = st.viewport.getBoundingClientRect();
      const viewCenterX = viewRect.left + viewRect.width / 2;
      let best = { idx: 0, d: Infinity };
      slides.forEach((el, i) => {
        const r = el.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const d = Math.abs(cx - viewCenterX);
        if (d < best.d) best = { idx: i, d };
      });
      return best.idx;
    }

    // しきい値付きの中心決定：改善幅が閾値未満なら前回indexを維持
    function centerIndexWithHysteresis(st, prevIndex, thresholdPx = 12) {
      const slides = Array.from(st.viewport.querySelectorAll('.slider__slide:not([data-clone="1"])'));
      if (!slides.length) return 0;

      const viewRect = st.viewport.getBoundingClientRect();
      const viewCenterX = viewRect.left + viewRect.width / 2;

      const dist = slides.map(el => {
        const r = el.getBoundingClientRect();
        return Math.abs((r.left + r.width/2) - viewCenterX);
      });

      // 現在のベスト候補
      let bestIdx = 0, bestD = dist[0];
      for (let i=1;i<dist.length;i++){
        if (dist[i] < bestD) { bestIdx = i; bestD = dist[i]; }
      }
      if (typeof prevIndex !== 'number' || prevIndex < 0) return bestIdx;

      const prevD = dist[prevIndex];
      // 改善がしきい値未満 → 前回indexを維持（境界のフラつき抑制）
      if (prevD - bestD < thresholdPx) return prevIndex;
      return bestIdx;
    }

    //25/10/16更新：開始位置是正対応
    function updateCurrentFromScroll() {
      if (st.unit <= 0 || st.realCount <= 0) return;
      st.current = centerIndexWithHysteresis(st, st.current, 12);
      updateActiveUI();
    }

    //25/10/16更新：開始位置是正対応
    function updateActiveUI() {
      const center = st.current;
      // 1フレーム後にドット反映（Safari/慣性対策で2フレ重ね）
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const dots = Array.from(root.querySelectorAll('.slider-counter__link'));
          dots.forEach((d, i) => {
            const on = i === center;
            d.classList.toggle('is-active', on);
            d.setAttribute('aria-current', on ? 'true' : 'false');
          });
        });
      });
    }

    // ===== CSS var <-> data =====
    function applyVars() {
      /* const peek = (isDesktop() ? st.peekD : st.peekM) + 'px';
      viewport.style.setProperty('--cols', String(st.cols), 'important');
      viewport.style.setProperty('--gap', st.gap + 'px', 'important');
      viewport.style.setProperty('--peek', peek, 'important'); */
    }

    function readData() {
      st.arrowsMode = root.getAttribute('data-arrows') === 'slide' ? 'slide' : 'page';
      st.loop = root.getAttribute('data-loop') !== 'false';

      // CSS変数を読む
      const cs = getComputedStyle(viewport);
      const colsVal = parseFloat(cs.getPropertyValue('--cols')) || 1;
      const gapVal  = parseFloat(cs.getPropertyValue('--gap'))  || 0;
      const peekStr = cs.getPropertyValue('--peek').trim();     // 例: "5%" or "40px"

      st.cols = Math.max(1, Math.round(colsVal));
      st.gap  = gapVal;

      // --peek は % も来るので最終pxに解決
      if (peekStr.endsWith('%')) {
        const p = parseFloat(peekStr) || 0;
        st.peekPx = viewport.clientWidth * (p / 100);
      } else {
        st.peekPx = parseFloat(peekStr) || 0;
      }

      applyVars();
    }

    function measureUnit() {
      const reals = qReal();
      st.realCount = reals.length;

      if (reals.length >= 2) {
        st.unit = Math.round(reals[1].offsetLeft - reals[0].offsetLeft); // (itemW + gap)
      } else {
        const w = reals[0]?.getBoundingClientRect().width || 0;
        st.unit = Math.round(w + st.gap);
      }
      // itemW = unit - gap（負にならないようガード）
      st.itemW = Math.max(1, st.unit - st.gap);
    }

    function removeClones() {
      st.viewport.querySelectorAll('.slider__slide[data-clone="1"]').forEach(n => n.remove());
      st.clones = 0;
    }
    function makeClones() {
      removeClones();
      if (!st.loop) return;
      const reals = qReal();
      if (reals.length <= 1) return;

      // 常に列数ぶんだけクローンを作る（1列でも1枚）
      const K = Math.max(1, st.cols);
      // 列数が 1 のときは、最低でも 2 枚ずつクローンを作る（帯を広げて初期ワープを防ぐ）
      // const K = st.cols <= 1 ? 2 : Math.max(1, st.cols);

      const mk = el => {
        const c = el.cloneNode(true);
        c.dataset.clone = '1';
        c.setAttribute('aria-hidden', 'true');
        c.removeAttribute('id');
        c.querySelectorAll('[id]').forEach(x => x.removeAttribute('id'));
        return c;
      };
      const head = reals.slice(0, K).map(mk);
      const tail = reals.slice(-K).map(mk);
      tail.forEach(c => st.viewport.insertBefore(c, reals[0]));
      head.forEach(c => st.viewport.appendChild(c));
      st.clones = K;
    }

    // 実スライド realIndex の“中央”をビューポート中央に合わせる scrollLeft を返す
    function centerXForRealIndex(realIndex) {
      const slides = qSlides();
      const idx = st.clones + realIndex;           // 先頭にクローンが並ぶので補正
      const target = slides[idx];
      if (!target) return 0;

      const left  = target.offsetLeft;             // 子の左端（viewport の padding を含む座標）
      const wItem = target.getBoundingClientRect().width;
      const wView = st.viewport.clientWidth;

      // offsetLeft は padding-left を含むので、peek を“足さない”のが正解
      let x = left - (wView - wItem) / 2;

      // スクロール可能範囲にクランプ
      const maxX = st.viewport.scrollWidth - wView;
      if (x < 0) x = 0;
      if (x > maxX) x = maxX;
      return Math.round(x);
    }

    function setStart() {
      if (!st.viewport) return;
      st.jumping = true;
      st.root.classList.add('is-setting');

      const START_INDEX = 0;
      const go = () => {
        measureUnit();
        const x0 = centerXForRealIndex(START_INDEX) || 0;
        st.viewport.scrollTo({ left: x0, behavior: 'auto' });
        st.current = START_INDEX;
        updateCurrentFromScroll();
        updateActiveUI();

        requestAnimationFrame(() => {
          const x1 = centerXForRealIndex(START_INDEX) || 0;
          st.viewport.scrollTo({ left: x1, behavior: 'auto' });
          st.current = START_INDEX;
          updateCurrentFromScroll();
          updateActiveUI();

          st.jumping = false;
          st.root.classList.remove('is-setting');
          st.root.classList.add('is-ready');
          st.ready = true;
          // mountDebugHUD(st);  //デバッグしたい時に有効化

          // 初期帯の軽いゆらぎを即補正
          normalizeLoop();
        });
      };

      let done = false;
      const fallback = setTimeout(() => { if (!done) { done = true; go(); } }, 300);
      const imgs = st.viewport.querySelectorAll('img');
      if (!imgs.length) { clearTimeout(fallback); done = true; go(); return; }
      let left = imgs.length;
      imgs.forEach(img => {
        if (img.complete) { if (--left === 0 && !done) { clearTimeout(fallback); done = true; go(); } }
        else {
          img.addEventListener('load', () => { if (--left === 0 && !done) { clearTimeout(fallback); done = true; go(); } }, { once:true });
          img.addEventListener('error',() => { if (--left === 0 && !done) { clearTimeout(fallback); done = true; go(); } }, { once:true });
        }
      });
    }

    // ===== loop normalize（縦スク中は停止） =====
    function normalizeLoop() {   
      if (!st.loop || st.unit <= 0 || st.realCount <= 0) return;
      if (st.isVerticalScroll || !st.ready) return;
      if (st.jumping || st.jumpingWrap) return;
      
      const N    = st.realCount;
      const span = N * st.unit;
      const base = centerXForRealIndex(0);
      const end  = base + span;
      const x    = st.viewport.scrollLeft;

      // ゆるめの閾値（誤爆防止）
      const EPS = Math.max(6, Math.round(st.unit * 0.16));

      let didWrap = false;
      st.jumpingWrap = true; // ← 一時フラグ（操作ロックの温床にならない軽い旗）
      
      if (x > end - EPS) {
        const x0 = centerXForRealIndex(0);
        st.viewport.scrollTo({ left: x0, behavior: 'auto' });
        st.current = 0;                 // 1枚目を明示
        didWrap = true;

      } else if (x < base - EPS) {
        // 最初→最後の折り返し
        const xLast = centerXForRealIndex(N - 1);
        st.viewport.scrollTo({ left: xLast, behavior: 'auto' });
        st.current = N - 1;
        didWrap = true;
      } 

        if (didWrap) {
          // 次フレで state を整える（ここで return してもロックは残らない）
          requestAnimationFrame(() => {
            st.jumpingWrap = false;
            updateCurrentFromScroll();
            updateActiveUI();
          });
        } else {
          st.jumpingWrap = false;
          updateCurrentFromScroll();
        }
      // ★ ロック解除（次フレームで解除してチラつき回避）
      //requestAnimationFrame(() => { st._loopLock = false; });
    }


    // ===== 操作系（②から全部採用） =====
    function stripHandlers(el){
      if (!el) return null;
      const clone = el.cloneNode(true);
      el.replaceWith(clone);
      return clone;
    }

    function wireArrows() {
      const wrap = root.querySelector('[data-dag-controls]') || root;
      let prev = stripHandlers(wrap.querySelector('.slider-button--prev'));
      let next = stripHandlers(wrap.querySelector('.slider-button--next'));

      const go = (dir) => {
        const cur = (typeof st.current === 'number') ? st.current : centerIndexByDistance(st);
        const N = st.realCount;
        if (N <= 0) return;

        // ★ 境目だけは即着地ジャンプ
        if (dir > 0 && cur === N - 1) {
          return jumpToIndexExact(0);
        }
        if (dir < 0 && cur === 0) {
          return jumpToIndexExact(N - 1);
        }

        const target = (cur + (dir > 0 ? 1 : -1) + N) % N;
        scrollToCenterIndex(target);
      };

      if (prev) prev.addEventListener('click', (e)=>{ e.preventDefault(); e.stopImmediatePropagation(); go(-1); }, {passive:false});
      if (next) next.addEventListener('click', (e)=>{ e.preventDefault(); e.stopImmediatePropagation(); go(+1); }, {passive:false});

      // 上バナー（必要なときだけ効く）
      const ovPrev = root.querySelector('.dag-ov__prev');
      const ovNext = root.querySelector('.dag-ov__next');
      if (ovPrev) ovPrev.addEventListener('click', (e)=>{ e.preventDefault(); e.stopImmediatePropagation(); go(-1); }, {passive:false});
      if (ovNext) ovNext.addEventListener('click', (e)=>{ e.preventDefault(); e.stopImmediatePropagation(); go(+1); }, {passive:false});
    }

    function wireDots() {
      const dots = Array.from(root.querySelectorAll('.slider-counter__link'));
      if (!dots.length) return;
      dots.forEach((btn, i) => {
        const b = stripHandlers(btn) || btn;
        b.addEventListener('click', (e) => {
          e.preventDefault(); e.stopImmediatePropagation();
          if (st.unit <= 0) return;
          scrollToCenterIndex(i);
        }, { passive: false });
      });
    }

    function wireClickToStepOnViewport() {
      const el = st.viewport;
      if (!el) return;
      const isInteractive = (t) => !!t.closest('a, button, .slider-counter__link, .custom-slide-button');
      el.addEventListener('click', (e) => {
        if (isInteractive(e.target)) return;
        if (st.unit <= 0 || st.realCount <= 0) return;
        const rect = el.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const leftZone  = rect.width * 0.33;
        const rightZone = rect.width * 0.67;

        if (x < leftZone) {
          const cur = (typeof st.current === 'number') ? st.current : centerIndexByDistance(st);
          scrollToCenterIndex((cur - 1 + st.realCount) % st.realCount);
          } else if (x > rightZone) {
          const cur = (typeof st.current === 'number') ? st.current : centerIndexByDistance(st);
          scrollToCenterIndex((cur + 1) % st.realCount);
        }
        /*
        if (x < leftZone) {
          const cur = centerIndexFromScroll(st.viewport.scrollLeft);
          scrollToCenterIndex((cur - 1 + st.realCount) % st.realCount);
        } else if (x > rightZone) {
          const cur = centerIndexFromScroll(st.viewport.scrollLeft);
          scrollToCenterIndex((cur + 1) % st.realCount);
        }
          */
      }, { passive: true });
    }

    function wireAutoplay(){
      const origAuto = String(st.viewport.getAttribute('data-autoplay')) === 'true';
      const speedSec = parseInt(st.viewport.getAttribute('data-speed'), 10) || 5;

      st.viewport.setAttribute('data-autoplay', 'false');

      let timer = null;
      const start = ()=> {
        if (timer || !origAuto) return;
        timer = setInterval(()=> {
          if (st.isVerticalScroll) return;
          const cur = (typeof st.current === 'number') ? st.current : centerIndexByDistance(st);
          const N = st.realCount;
          if (N <= 0) return;

          if (cur === N - 1) {
            // ★ 最終 → 最初 だけは即着地（オーバーシュートを発生させない）
            jumpToIndexExact(0);
          } else {
            scrollToCenterIndex((cur + 1) % N);
          }
        }, speedSec * 1000);
      };
      const stop = ()=> { if (!timer) return; clearInterval(timer); timer = null; };

      st.off.push(on(st.root, 'mouseenter', stop,   { passive:true }));
      st.off.push(on(st.root, 'focusin',    stop,   { passive:true }));
      st.off.push(on(st.root, 'mouseleave', start,  { passive:true }));
      st.off.push(on(st.root, 'focusout',   start,  { passive:true }));

      const vis = ()=> document.hidden ? stop() : start();
      document.addEventListener('visibilitychange', vis);
      st.off.push(()=> document.removeEventListener('visibilitychange', vis));

      // ▶ 追加：可視範囲でのみ再生
      attachVisibilityAutoplay(st, start, stop);

      start();
    }

    // ===== ①の“縦スク検知”を統合 =====
    st.off.push(on(st.viewport, 'touchstart', (e) => {
      if (!e.touches || e.touches.length === 0) return;
      st.touchStartX = e.touches[0].clientX;
      st.touchStartY = e.touches[0].clientY;
      st.isVerticalScroll = false;
    }, { passive: true }));

    st.off.push(on(st.viewport, 'touchmove', (e) => {
      if (!e.touches || e.touches.length === 0) return;
      const dx = Math.abs(e.touches[0].clientX - st.touchStartX);
      const dy = Math.abs(e.touches[0].clientY - st.touchStartY);
      if (dy > dx + 2) st.isVerticalScroll = true; // 縦優勢
    }, { passive: true }));

    st.off.push(on(st.viewport, 'touchend', () => {
      // 慣性スク考慮して少し待って解除
      setTimeout(()=>{ st.isVerticalScroll = false; }, 120);
    }, { passive: true }));
    st.off.push(on(st.viewport, 'touchcancel', () => {
      setTimeout(()=>{ st.isVerticalScroll = false; }, 120);
    }, { passive: true }));

    // ===== scroll =====
    st.off.push(on(st.viewport, 'scroll', () => {
      clearTimeout(st.timers.scrollEnd);
      st.timers.scrollEnd = setTimeout(() => {
        // 追記: 直近の速度がほぼゼロかチェックしてから normalize
        const cur = st.viewport.scrollLeft;
        const prev = st._lastX ?? cur;
        const v = Math.abs(cur - prev);
        st._lastX = cur;

      if (!st.isVerticalScroll && !st.jumping && v < 1) {
        normalizeLoop();   // ← 慣性停止“後”だけ補正
        updateActiveUI(); 
      }
        updateCurrentFromScroll();
      }, (window.matchMedia('(max-width: 989px)').matches ? 160 : 90));
    }, { passive:true }));

    // ===== reflow/init =====
    function reflow() { readData(); removeClones(); makeClones(); measureUnit(); setStart(); }
    reflow();
    wireArrows();
    wireDots();
    wireClickToStepOnViewport();
    wireAutoplay();

    const onResize = () => { clearTimeout(st.timers.scrollEnd); setTimeout(reflow, 120); };
    st.off.push(on(window, 'resize', onResize));
    mqDesktop.addEventListener('change', onResize);
    st.off.push(() => mqDesktop.removeEventListener('change', onResize));
  }

  function destroyOne(root) {
    const st = S.get(root);
    if (!st) return;
    st.off.forEach(off => { try { off(); } catch (_) {} });
    st.viewport && st.viewport.querySelectorAll('.slider__slide[data-clone="1"]').forEach(n => n.remove());
    S.delete(root);
  }

  function initAll(scope = document) { scope.querySelectorAll(SELECTOR).forEach(initOne); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  document.addEventListener('shopify:section:load',   (e) => initAll(e.target));
  document.addEventListener('shopify:section:unload', (e) => e.target.querySelectorAll(SELECTOR).forEach(destroyOne));
})();

/* ---- dAgora minimal patch: ensure full-slide links on clones without replacing existing JS ---- */
(() => {
  const SECTION_SEL = '[data-dag-slider]';
  const SLIDE_SEL   = '.slider__slide';
  const HITAREA_SEL = '.slide-hitarea';

  function ensureHitarea(slide) {
    if (!(slide instanceof HTMLElement)) return;
    // すでにヒットエリアがあれば何もしない
    if (slide.querySelector(HITAREA_SEL)) return;

    // Liquidで埋めた data-link を読む
    const href = slide.dataset.link;
    if (!href) return;

    // 全面リンクを生成
    const a = document.createElement('a');
    a.className = 'slide-hitarea';
    a.href = href;

    // アクセシビリティ用ラベル（見出しがあれば借りる）
    const heading = slide.querySelector('.banner__heading');
    if (heading && heading.textContent) {
      a.setAttribute('aria-label', heading.textContent.trim());
    }

    slide.appendChild(a);
  }

  function initSection(section) {
    // 既存スライドに適用
    section.querySelectorAll(SLIDE_SEL).forEach(ensureHitarea);

    // クローン等の動的追加に追従
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.matches && node.matches(SLIDE_SEL)) {
            ensureHitarea(node);
          }
          // まとめて追加されるケースにも対応
          node.querySelectorAll && node.querySelectorAll(SLIDE_SEL).forEach(ensureHitarea);
        });
      }
    });
    mo.observe(section, { childList: true, subtree: true });
  }

  function init() {
    document.querySelectorAll(SECTION_SEL).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


window.centerIndexByDistance = function(st){
  const slides = Array.from(st.viewport.querySelectorAll('.slider__slide:not([data-clone="1"])'));
  if (!slides.length) return 0;
  const viewRect = st.viewport.getBoundingClientRect();
  const viewCenterX = viewRect.left + viewRect.width / 2;
  let best = { idx: 0, d: Infinity };
  slides.forEach((el, i) => {
    const r = el.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const d = Math.abs(cx - viewCenterX);
    if (d < best.d) best = { idx: i, d };
  });
  return best.idx;
};


// ==== DEBUG HUD (temporary) =====================================
function mountDebugHUD(st){
  if (!st || st.debugHud) return;
  const hud = document.createElement('div');
  hud.className = 'dag-debug';
  hud.innerHTML = `
    <div><strong>Slideshow Debug</strong> (press "d" to toggle)</div>
    <div>scrollLeft: <span data-k="x"></span></div>
    <div>unit: <span data-k="u"></span> / realCount: <span data-k="n"></span></div>
    <div>centerIdx(dist): <span data-k="ci"></span> / dotIdx: <span data-k="di"></span></div>
    <div>viewCenterX: <span data-k="vc"></span></div>
    <div>deltaToEach: <span data-k="dd"></span></div>
  `;
  Object.assign(hud.style, {
    position:'fixed', top:'8px', left:'8px', zIndex:99999,
    font:'12px/1.45 ui-monospace, SFMono-Regular, Menlo, monospace',
    background:'rgba(0,0,0,.7)', color:'#fff', padding:'8px 10px', borderRadius:'8px',
    pointerEvents:'none', userSelect:'none'
  });
  document.body.appendChild(hud);
  st.debugHud = hud;

  const $ = k => hud.querySelector(`[data-k="${k}"]`);
  const tick = () => {
    if (!st.debugHud) return;
    const slides = Array.from(st.viewport.querySelectorAll('.slider__slide:not([data-clone="1"])'));
    const viewRect = st.viewport.getBoundingClientRect();
    const viewCenterX = viewRect.left + viewRect.width/2;

    const center = centerIndexByDistance(st);
    const dots = Array.from(st.root.querySelectorAll('.slider-counter__link'));
    const activeDot = dots.findIndex(d => d.classList.contains('is-active'));

    const deltas = slides.map((el,i)=>{
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width/2;
      return Math.round(cx - viewCenterX);
    });

    $('x').textContent  = Math.round(st.viewport.scrollLeft);
    $('u').textContent  = Math.round(st.unit || 0);
    $('n').textContent  = st.realCount || 0;
    $('ci').textContent = center;
    $('di').textContent = activeDot;
    $('vc').textContent = Math.round(viewCenterX);
    $('dd').textContent = `[${deltas.join(', ')}]`;

    // 可視化：今センターの実スライドに枠線
    slides.forEach((el,i)=> el.classList.toggle('is-center-debug', i===center));
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);

  // toggle（dキー）
  const onKey = (e)=> {
    if (e.key.toLowerCase() === 'd') {
      hud.style.display = (hud.style.display === 'none') ? '' : 'none';
    }
  };
  document.addEventListener('keydown', onKey);
  st.off.push(()=> {
    document.removeEventListener('keydown', onKey);
    if (st.debugHud && st.debugHud.parentNode) st.debugHud.parentNode.removeChild(st.debugHud);
    st.debugHud = null;
  });
}
// ==== / DEBUG HUD ===============================================
